import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import {
    CreateExternalPricelistDto,
    ErrorResponse,
    ExternalPricelistDto,
    UpdateExternalPricelistDto
} from '../../../common/PricePrgClient';
import { createAsyncAction } from '../../../utils';
import { deletePricelist } from '../../pricelist/redux/pricelistSlices';

const namespaces = {
    externalPriceDialogs: 'externalPriceDialogs',
    externalPricelists: 'externalPricelists',
    selectedExternalPricelistId: 'selectedExternalPricelistId',
    createExternalPricelistDialog: 'createExternalPricelistDialog',
    externalPricelistCustomerIds: 'externalPricelistCustomerIds'
};
const externalPricelistsEntity = new schema.Entity<ExternalPricelistDto>('externalPricelists', undefined, {
    idAttribute: 'externalPricelistId'
});

const externalPriceDialogsSlice = createSlice({
    name: namespaces.externalPriceDialogs,
    initialState: {
        isChooseExternalPricelistsDialogOpen: false
    },
    reducers: {
        openChooseExternalPricelistsDialog: state => {
            state.isChooseExternalPricelistsDialogOpen = true;
        },
        closeChooseExternalPricelistsDialog: state => {
            state.isChooseExternalPricelistsDialogOpen = false;
        }
    }
});

export const { closeChooseExternalPricelistsDialog, openChooseExternalPricelistsDialog } =
    externalPriceDialogsSlice.actions;

export const fetchExternalPricelists = createAsyncAction<void, ExternalPricelistDto[]>(
    namespaces.externalPricelists,
    'fetchExternalPricelists'
);
export const updateExternalPricelist = createAsyncAction<
    UpdateExternalPricelistDto,
    UpdateExternalPricelistDto
>(namespaces.externalPricelists, 'updateExternalPriceList');
export const createExternalPricelist = createAsyncAction<
    CreateExternalPricelistDto,
    ExternalPricelistDto,
    ErrorResponse
>(namespaces.externalPricelists, 'createExternalPricelist');
export const removeExternalPricelist = createAsyncAction<number, number>(
    namespaces.externalPricelists,
    'removeExternalPricelist'
);

const externalPricelistsSlice = createSlice({
    name: namespaces.externalPricelists,
    initialState: {} as { [externalPricelistId: number]: ExternalPricelistDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(deletePricelist.success, (state, action) => {
                for (const key in state) {
                    if (state[key].pricelistId === action.payload) {
                        delete state[key];
                    }
                }
            })
            .addCase(fetchExternalPricelists.success, (state, action) => {
                const normalized = normalize(action.payload, [externalPricelistsEntity]);
                return normalized.entities.externalPricelists ?? {};
            })
            .addCase(updateExternalPricelist.success, (state, action) => {
                const { payload } = action;
                state[payload.externalPricelistId].pricelistId = payload.inPricelistId;
                state[payload.externalPricelistId].includeVat = payload.includeVat;
                state[payload.externalPricelistId].marginThreshold = payload.marginThreshold;
                state[payload.externalPricelistId].name = payload.name;
            })
            .addCase(createExternalPricelist.success, (state, action) => {
                state[action.payload.externalPricelistId] = action.payload;
            })
            .addCase(removeExternalPricelist.success, (state, action) => {
                delete state[action.payload];
            })
});

const selectedExternalPricelistIdSlice = createSlice({
    name: namespaces.selectedExternalPricelistId,
    initialState: null as number | null,
    reducers: {
        externalPricelistsSelected: (state, action: PayloadAction<number>) => action.payload ?? []
    }
});

export const { externalPricelistsSelected } = selectedExternalPricelistIdSlice.actions;

type ExternalPricelistDialogState = {
    isOpen: boolean;
    isCreating: boolean;
};

const createExternalPricelistDialogSlice = createSlice({
    name: namespaces.createExternalPricelistDialog,
    initialState: {
        isOpen: false,
        isCreating: false
    } as ExternalPricelistDialogState,
    reducers: {
        openCreateExternalPricelistDialog: state => {
            state.isOpen = true;
        },
        closeCreateExternalPricelistDialog: state => {
            state.isOpen = false;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(createExternalPricelist.request, state => {
                state.isCreating = true;
            })
            .addCase(createExternalPricelist.success, state => {
                state.isCreating = false;
            })
            .addCase(createExternalPricelist.failure, state => {
                state.isCreating = false;
            })
});

export const { openCreateExternalPricelistDialog, closeCreateExternalPricelistDialog } =
    createExternalPricelistDialogSlice.actions;

export default combineReducers({
    externalPricelists: externalPricelistsSlice.reducer,
    ui: combineReducers({
        externalPricelistDialog: externalPriceDialogsSlice.reducer,
        selectedExternalPricelistId: selectedExternalPricelistIdSlice.reducer,
        createExternalPricelistDialog: createExternalPricelistDialogSlice.reducer
    })
});
